import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  getHeroTier, getNextHeroTier, getProgressToNextTier,
  getHeroClass, getHeroImage, HERO_CLASSES,
  getCoreBranches, sumBranchPoints, maxBranchPoints, getAchievementDef,
  TIER_STORIES, calculatePlaytomicRatingSmooth,
} from './heroClasses';
import SkillTreeVisualization from './SkillTreeVisualization';
import PrioritySkillsSection from '../Skills/PrioritySkillsSection';
import CoachPriorityAssignModal from '../CoachView/CoachPriorityAssignModal';
import './StatsDisplay.css';

// ── Radar / Spider Chart (DEF / ATT / TAC / BRAWN) ────────────────────────────────────
function RadarChart({ defPct, attPct, tacPct, brawnPct, classColor }) {
  // viewBox is 400×420; labels sit at r+32 so we need that padding on all sides
  const cx = 200, cy = 210, r = 145;

  // Re-play the reveal animation every time the chart scrolls into view
  // (not just once): watch visibility, and bump a key each time it
  // transitions from off-screen to on-screen so the animated group
  // remounts and its CSS animation restarts from the beginning.
  const containerRef = useRef(null);
  const wasVisibleRef = useRef(false);
  const [animKey, setAnimKey] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !wasVisibleRef.current) {
          wasVisibleRef.current = true;
          setAnimKey(k => k + 1);
        } else if (!entry.isIntersecting) {
          wasVisibleRef.current = false;
        }
      },
      { threshold: 0.4 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Branch colors matching the skill tree
  const branchColors = [
    { label: 'Defense', angleDeg: -90, color: '#22c55e', rgb: '34, 197, 94' },         // Green
    { label: 'Attack', angleDeg: 0, color: '#ef4444', rgb: '239, 68, 68' },           // Red
    { label: 'Brawn', angleDeg: 90, color: '#fb923c', rgb: '251, 146, 60' },    // Orange
    { label: 'Tactics', angleDeg: 180, color: '#38bdf8', rgb: '56, 189, 248' },        // Blue
  ];
  const pcts = [defPct, attPct, brawnPct, tacPct];

  const toXY = (angleDeg, dist) => {
    const rad = (angleDeg * Math.PI) / 180;
    return { x: cx + dist * Math.cos(rad), y: cy + dist * Math.sin(rad) };
  };

  const gridLevels = [0.25, 0.5, 0.75, 1];
  const dataPoints = branchColors.map((branch, i) => toXY(branch.angleDeg, r * Math.max(0.05, pcts[i])));

  return (
    <div className="st-radar" ref={containerRef}>
      <svg viewBox="0 0 400 420" width="100%" style={{ maxWidth: 400 }}>
        {/* Grid circles */}
        {gridLevels.map((lvl, li) => {
          const pts = branchColors.map(branch => toXY(branch.angleDeg, r * lvl));
          return (
            <polygon
              key={`${lvl}-${animKey}`}
              className="st-radar__ring"
              style={{ animationDelay: `${li * 0.12}s` }}
              points={pts.map(p => `${p.x},${p.y}`).join(' ')}
              fill="none"
              stroke="rgba(212,165,116,0.18)"
              strokeWidth="1.5"
            />
          );
        })}

        {/* Data group (segments + axis lines + dots) - remounted (via key)
            every time the chart re-enters the viewport so its CSS reveal
            animation replays. transform-origin is the chart center
            (cx=200, cy=210 in a 400x420 viewBox = exactly 50%/50%), so it
            scales in from the middle rather than the SVG's top-left. */}
        <g key={animKey} className="st-radar__data">
          {/* Colored segments for each branch */}
          {branchColors.map((branch, i) => {
            const nextIndex = (i + 1) % branchColors.length;
            const nextBranch = branchColors[nextIndex];
            const p1 = dataPoints[i];
            const p2 = dataPoints[nextIndex];

            // Create a segment polygon: center -> p1 -> p2 -> center
            const segmentPoints = `${cx},${cy} ${p1.x},${p1.y} ${p2.x},${p2.y}`;

            return (
              <polygon
                key={`segment-${i}`}
                points={segmentPoints}
                fill={`${branch.color}25`}
                stroke="none"
              />
            );
          })}

          {/* Colored axis lines */}
          {branchColors.map((branch, i) => {
            const tip = dataPoints[i];
            return (
              <line key={`axis-${i}`} x1={cx} y1={cy} x2={tip.x} y2={tip.y}
                stroke={branch.color} strokeWidth="2.5" opacity="0.8" />
            );
          })}

          {/* Data point dots - colored by branch */}
          {dataPoints.map((p, i) => (
            <circle key={`dot-${i}`} cx={p.x} cy={p.y} r="7"
              fill={branchColors[i].color} stroke="white" strokeWidth="2" />
          ))}
        </g>

        {/* Axis labels with branch colors */}
        {branchColors.map((branch, i) => {
          const labelPos = toXY(branch.angleDeg, r + 32);
          const pctLabel = Math.round(pcts[i] * 100);
          return (
            <g key={`${branch.label}-${animKey}`} className="st-radar__label" style={{ animationDelay: `${0.5 + i * 0.1}s` }}>
              <text x={labelPos.x} y={labelPos.y - 6}
                textAnchor="middle" fill={branch.color} fontSize="14" fontWeight="700" fontFamily="Cinzel, serif">
                {branch.label}
              </text>
              <text x={labelPos.x} y={labelPos.y + 16}
                textAnchor="middle" fill={branch.color} fontSize="16" fontWeight="600" opacity="0.8">
                {pctLabel}%
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// Achievement Popup - Now with navigation for multiple achievements
function AchievementPopup({ achievement, allAchievements = [], currentIndex = 0, onClose, onNext, onPrev }) {
  if (!achievement) return null;

  const CLASS_COLORS = {
    guardian: '#60a5fa', berserker: '#f87171', strategist: '#c084fc', duelist: '#fbbf24', cross: '#4ade80',
  };
  const CLASS_LABELS = {
    guardian: 'Guardian', berserker: 'Berserker', strategist: 'Strategist', duelist: 'Duelist', cross: 'All Classes',
  };

  const classColor = CLASS_COLORS[achievement.class] || '#d4a574';
  const classLabel = CLASS_LABELS[achievement.class] || achievement.class;
  const totalAchievements = allAchievements.length;
  const hasMultiple = totalAchievements > 1;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="ach-popup-overlay" onClick={handleBackdropClick}>
      <div className="ach-popup">
        <button className="ach-popup__close" onClick={onClose} aria-label="Close">✕</button>

        {/* Counter showing current/total achievements */}
        {hasMultiple && (
          <div className="ach-popup__counter">
            {currentIndex + 1} / {totalAchievements}
          </div>
        )}

        <div className="ach-popup__icon">{achievement.icon}</div>
        <h2 className="ach-popup__name">{achievement.name}</h2>
        <div className="ach-popup__tags">
          <span className="ach-popup__class-tag" style={{ borderColor: classColor, color: classColor }}>
            {classLabel}
          </span>
          {achievement.tier5 && <span className="ach-popup__pinnacle">✨ Pinnacle</span>}
        </div>
        <div className="ach-popup__divider" />
        <p className="ach-popup__desc">{achievement.desc}</p>
        <div className="ach-popup__unlocked">✅ Achievement Unlocked</div>

        {/* Navigation buttons for multiple achievements */}
        {hasMultiple && (
          <div className="ach-popup__nav">
            <button
              className="ach-popup__nav-btn"
              onClick={onPrev}
              disabled={currentIndex === 0}
              aria-label="Previous achievement"
            >
              ← Prev
            </button>
            <button
              className="ach-popup__nav-btn"
              onClick={onNext}
              disabled={currentIndex === totalAchievements - 1}
              aria-label="Next achievement"
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Calculate player's court side based on skill distribution
function calculatePlayerSide(ratings) {
  let leftPoints = 0;
  let rightPoints = 0;

  // 1. Smash vs Vibora
  const smashIds = Object.keys(ratings).filter(id =>
    id.startsWith('tech-att-overhead-flat-') || id.startsWith('tech-att-overhead-topspin-')
  );
  const viboraIds = Object.keys(ratings).filter(id =>
    id.startsWith('tech-att-overhead-vibora')
  );
  const smashTotal = smashIds.reduce((sum, id) => sum + (ratings[id] || 0), 0);
  const viboraTotal = viboraIds.reduce((sum, id) => sum + (ratings[id] || 0), 0);

  if (smashTotal > viboraTotal) {
    leftPoints += 1;
  } else if (viboraTotal > smashTotal) {
    rightPoints += 1;
  }

  // 2. Forehand off glass vs Backhand off glass
  const fhGlassIds = Object.keys(ratings).filter(id => id.startsWith('tech-def-ofglass-fh'));
  const bhGlassIds = Object.keys(ratings).filter(id => id.startsWith('tech-def-ofglass-bh'));
  const fhGlassTotal = fhGlassIds.reduce((sum, id) => sum + (ratings[id] || 0), 0);
  const bhGlassTotal = bhGlassIds.reduce((sum, id) => sum + (ratings[id] || 0), 0);

  if (fhGlassTotal > bhGlassTotal) {
    rightPoints += 1;
  } else if (bhGlassTotal > fhGlassTotal) {
    leftPoints += 1;
  }

  // 3. Brawn: Balance across different athletic skills
  const speedIds = Object.keys(ratings).filter(id => id.startsWith('brawn-speed-'));
  const staminaIds = Object.keys(ratings).filter(id => id.startsWith('brawn-stamina-'));
  const speedTotal = speedIds.reduce((sum, id) => sum + (ratings[id] || 0), 0);
  const staminaTotal = staminaIds.reduce((sum, id) => sum + (ratings[id] || 0), 0);

  if (speedTotal > staminaTotal) {
    rightPoints += 1;
  } else if (staminaTotal > speedTotal) {
    leftPoints += 1;
  }

  return rightPoints > leftPoints ? 'right' : 'left';
}

// Stats Display Component - Shows hero tier, progression, and achievements
export default function StatsDisplay({ ratings = {}, achievements = [], isReadOnly = false, pointEvents = [], onFirstView, playerId, user, defaultGender = 'm', defaultHandedness = 'r', studentId = null }) {
  // user (the full profile) is only passed when viewing your own profile
  // (PlayerView.js) - a coach viewing a student's tree only gets
  // defaultGender/defaultHandedness (CoachView.js), since it doesn't have
  // that student's full user object handy. Prefer user's own value when
  // present, but don't just fall back to a hardcoded 'm'/'r' when it's
  // not - that silently showed every student as male regardless of their
  // real profile.
  const [gender, setGender] = useState(() => user?.gender || defaultGender);
  const [handedness, setHandedness] = useState(() => user?.handedness || defaultHandedness);
  const [selectedAchievementIndex, setSelectedAchievementIndex] = useState(null);
  const [barReady, setBarReady] = useState(false);
  const [heroExpanded, setHeroExpanded] = useState(false);
  const [activeStatsTab, setActiveStatsTab] = useState('points'); // 'points', 'skills', 'tier', 'achievements'
  const containerRef = useRef(null);
  const hasShownDiff = useRef(false);

  // Keep gender/handedness in sync with the real profile value, whichever
  // source it came from.
  useEffect(() => {
    setGender(user?.gender || defaultGender);
    setHandedness(user?.handedness || defaultHandedness);
  }, [user?.gender, user?.handedness, defaultGender, defaultHandedness]);

  // MOUNT LOG
  useEffect(() => {

    // Add global click listener to detect if clicks are reaching the page
    const handleAnyClick = (e) => {
      if (e.target.closest('.st-stat-box--clickable')) {
      }
    };

    document.addEventListener('click', handleAnyClick, true);
    return () => document.removeEventListener('click', handleAnyClick, true);
  }, []);

  // Log point events changes
  useEffect(() => {
    if (pointEvents.length > 0) {
      pointEvents.forEach(pe => {
      });
    }
  }, [pointEvents]);

  useEffect(() => {
    const t = setTimeout(() => setBarReady(true), 120);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {

    // Only set up observer in player view (read-only mode)
    if (!isReadOnly || !onFirstView) {
      return;
    }

    const el = containerRef.current;
    if (!el) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {

        if (entry.isIntersecting && !hasShownDiff.current) {
          hasShownDiff.current = true;
          onFirstView();
          observer.disconnect();
        }
      },
      {
        threshold: [0, 0.25, 0.5, 0.75, 1.0],
        rootMargin: '0px'
      }
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, [isReadOnly, onFirstView]);

  // Branch breakdown (calculate FIRST)
  const { defense, attack, tactics, brawn } = getCoreBranches();
  const defPts = sumBranchPoints(defense, ratings);
  const attPts = sumBranchPoints(attack, ratings);
  const tacPts = sumBranchPoints(tactics, ratings);
  const brawnPts = sumBranchPoints(brawn, ratings);
  const defMax = maxBranchPoints(defense) || 1;
  const attMax = maxBranchPoints(attack) || 1;
  const tacMax = maxBranchPoints(tactics) || 1;
  const brawnMax = maxBranchPoints(brawn) || 1;

  // Calculate total points from actual branch points (not raw ratings)
  const totalPoints = defPts + attPts + tacPts + brawnPts;

  // Get tier and class (updates in real-time as points change)
  const currentTier = getHeroTier(totalPoints);
  const nextTier = getNextHeroTier(totalPoints);
  const progressPercent = getProgressToNextTier(totalPoints);
  const heroClass = getHeroClass(ratings);
  const classInfo = HERO_CLASSES[heroClass] || {};
  const heroImage = getHeroImage(heroClass, currentTier.tier, gender);

  const CLASS_COLORS = {
    guardian: '#60a5fa', berserker: '#f87171', strategist: '#c084fc', duelist: '#fbbf24', cross: '#4ade80',
  };
  const classColor = CLASS_COLORS[heroClass] || '#4ade80';

  const unlockedSkills = Object.values(ratings).filter(r => r > 0).length;

  // Points to next tier (or previous tier if devolving)
  const pointsToNext = nextTier ? nextTier.minPoints - totalPoints : 0;
  const pointsToPrev = currentTier.tier > 0 ? totalPoints - currentTier.minPoints : 0;

  // Achievement navigation handlers
  const handleOpenAchievement = (index) => {
    setSelectedAchievementIndex(index);
  };

  const handleNextAchievement = () => {
    if (selectedAchievementIndex < achievements.length - 1) {
      setSelectedAchievementIndex(selectedAchievementIndex + 1);
    }
  };

  const handlePrevAchievement = () => {
    if (selectedAchievementIndex > 0) {
      setSelectedAchievementIndex(selectedAchievementIndex - 1);
    }
  };

  const currentAchievement = selectedAchievementIndex !== null ? achievements[selectedAchievementIndex] : null;

  // Memoize student object to prevent unnecessary re-renders of CoachPriorityAssignModal
  const memoizedStudent = useMemo(() => (
    studentId ? { _id: studentId, name: 'Student' } : null
  ), [studentId]);

  return (
    <div className="st-stats-display" ref={containerRef}>
      {/* Achievement detail popup - only render if achievement exists */}
      {currentAchievement && (
        <AchievementPopup
          achievement={currentAchievement}
          allAchievements={achievements}
          currentIndex={selectedAchievementIndex}
          onClose={() => setSelectedAchievementIndex(null)}
          onNext={handleNextAchievement}
          onPrev={handlePrevAchievement}
        />
      )}

      {/* Hero Image Fullscreen */}
      {heroExpanded && heroImage && (
        <div className="hero-expand-overlay" onClick={() => setHeroExpanded(false)}>
          <img src={heroImage} alt={`${classInfo.label} ${currentTier.name}`} className="hero-expand-img" />
        </div>
      )}

      {/* Hero Card */}
      <div className="st-hero-card">
        {/* Court Side Badge - Top Right */}
        {(() => {
          let playerSide = calculatePlayerSide(ratings);
          if (handedness === 'l') {
            playerSide = playerSide === 'right' ? 'left' : 'right';
          }
          return (
            <div className="st-court-side--top-right">
              <span className={`st-court-side__badge st-court-side__badge--${playerSide}`}>
                {playerSide === 'right' ? '→ RIGHT' : '← LEFT'}
              </span>
            </div>
          );
        })()}

        <div className="st-hero-card__header">
          {heroImage && (
            <>
              <div className="st-hero-card__image-wrapper">
                <div className="st-hero-card__image-container" onClick={() => setHeroExpanded(true)} style={{ cursor: 'pointer' }}>
                  <img
                    src={heroImage}
                    alt={`${classInfo.label} ${currentTier.name}`}
                    className="st-hero-card__image"
                  />
                </div>

                {/* Gender & Handedness Inline - read-only display of the
                    real profile values (this used to be a "toggle" whose
                    click handler only ever forced gender to 'm' and
                    handedness to 'r', regardless of which one you clicked -
                    it couldn't actually switch to female/left-handed, it
                    just looked like it could). */}
                <div className="st-attributes-row">
                  <div className="st-gender-toggle">
                    <span
                      className="st-gender-btn st-gender-btn--active"
                      style={{ cursor: 'default' }}
                      title={gender === 'm' ? 'Male' : 'Female'}
                    >{gender === 'm' ? '♂' : '♀'}</span>
                  </div>

                  <div className="st-handedness-toggle">
                    <span
                      className="st-handedness-btn st-handedness-btn--active"
                      style={{ cursor: 'default' }}
                      title={handedness === 'r' ? 'Right-handed' : 'Left-handed'}
                    >{handedness === 'r' ? 'R' : 'L'}</span>
                  </div>
                </div>
              </div>

              {/* Hero Class Info - Right Side */}
              <div className="st-hero-class-overlay" style={{ borderLeft: `4px solid ${classColor}` }}>
                <div className="st-hero-class-overlay__tier" style={{ color: classColor }}>
                  <span className="st-hero-class-overlay__tier-label">Tier {currentTier.tier + 1}</span>
                  <span className="st-hero-class-overlay__tier-name">{currentTier.name}</span>
                </div>
                <div className="st-hero-class-overlay__name" style={{ color: classColor }}>
                  {classInfo.label}
                </div>
                <div className="st-hero-class-overlay__desc">{classInfo.description}</div>

                {/* Playtomic Rating Display */}
                <div className="st-playtomic-rating">
                  <div className="st-playtomic-rating__label">Playtomic Rating</div>
                  <div className="st-playtomic-rating__value">
                    {calculatePlaytomicRatingSmooth(ratings).toFixed(2)}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Tier Progress */}
        {nextTier && barReady && (
          <div className="st-hero-card__progress-section">
            <div className="st-hero-card__progress-label">Progress to {nextTier.name} (Tier {nextTier.tier + 1})</div>
            <div className="st-hero-card__progress-bar-wrapper">
              <div className="st-hero-card__progress-bar-bg">
                <div
                  className="st-hero-card__progress-bar-fill"
                  style={{
                    width: `${progressPercent}%`,
                    backgroundColor: classColor,
                    transition: 'width 0.5s ease-out',
                  }}
                />
              </div>
            </div>
            <div className="st-hero-card__progress-text">{totalPoints} / {nextTier.minPoints} points</div>
          </div>
        )}

        {/* Devolution Warning (if close to demotion) */}
        {pointsToPrev < 3 && currentTier.tier > 0 && (
          <div className="st-devolution-warning">
            <div className="st-devolution-warning__label">⚠️ Demotion Risk</div>
            <div className="st-devolution-warning__bar">
              <div
                className="st-devolution-warning__fill"
                style={{ width: `${((3 - pointsToPrev) / 3) * 100}%` }}
              />
            </div>
            <div className="st-devolution-warning__text">
              {pointsToPrev} points until demotion to {currentTier.tier > 0 ? getHeroTier(currentTier.minPoints - 1).name : 'Novice'}
            </div>
          </div>
        )}

        {/* Maximum Tier */}
        {!nextTier && (
          <div className="st-hero-card__progress-section">
            <div className="st-hero-card__progress-label">🏆 Maximum Tier Reached!</div>
            <div className="st-hero-card__progress-text">Total: {totalPoints} points</div>
          </div>
        )}
      </div>

      {/* Radar Chart */}
      <RadarChart
        defPct={defPts / defMax}
        attPct={attPts / attMax}
        brawnPct={brawnPts / brawnMax}
        tacPct={tacPts / tacMax}
        classColor={classColor}
      />

      {/* Branch Stats Row — DEF / ATT / BRAWN / TAC with point event counters */}
      <div className="st-branch-row">
        {[
          { key: 'defense', label: 'Defense', pts: defPts, max: defMax },
          { key: 'attack', label: 'Attack', pts: attPts, max: attMax },
          { key: 'brawn', label: 'Brawn', pts: brawnPts, max: brawnMax },
          { key: 'tactics', label: 'Tactics', pts: tacPts, max: tacMax },
        ].map(({ key, label, pts, max }) => {
          const branchEvents = pointEvents.filter(e => e.branch === key);
          if (branchEvents.length > 0) {
          }
          return (
            <div key={key} className={`st-stat-box st-stat-box--${key}`}>
              <div className="st-stat-box__label">{label}</div>
              <div className="st-stat-box__value">{pts}<span className="st-stat-box__max">/{max}</span></div>
              {branchEvents.map(e => (
                <span
                  key={e.id}
                  className={`point-event ${e.diff > 0 ? 'point-event--pos' : 'point-event--neg'}`}
                  style={{ animationDuration: `${e.duration || 1400}ms` }}
                >
                  {e.diff > 0 ? `+${e.diff}` : e.diff}
                </span>
              ))}
            </div>
          );
        })}
      </div>

      {/* Secondary Stats Grid - Tab Navigation */}
      <div className="st-stats-grid">
        <div
          className={`st-stat-box st-stat-box--tab ${activeStatsTab === 'points' ? 'st-stat-box--active' : ''}`}
          onClick={() => setActiveStatsTab(activeStatsTab === 'points' ? null : 'points')}
          role="tab"
          tabIndex={0}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setActiveStatsTab(activeStatsTab === 'points' ? null : 'points')}
          style={{ cursor: 'pointer' }}
        >
          <div className="st-stat-box__label">Total Points</div>
          <div className="st-stat-box__value">{totalPoints}</div>
        </div>
        <div
          className={`st-stat-box st-stat-box--tab ${activeStatsTab === 'skills' ? 'st-stat-box--active' : ''}`}
          onClick={() => {
            setActiveStatsTab(activeStatsTab === 'skills' ? null : 'skills');
          }}
          role="tab"
          tabIndex={0}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setActiveStatsTab(activeStatsTab === 'skills' ? null : 'skills')}
          style={{ cursor: 'pointer' }}
        >
          <span className="st-stat-box__label">Skills Unlocked</span>
          <span className="st-stat-box__value">{unlockedSkills}</span>
        </div>
        <div
          className={`st-stat-box st-stat-box--tab ${activeStatsTab === 'tier' ? 'st-stat-box--active' : ''}`}
          onClick={() => setActiveStatsTab(activeStatsTab === 'tier' ? null : 'tier')}
          role="tab"
          tabIndex={0}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setActiveStatsTab(activeStatsTab === 'tier' ? null : 'tier')}
          style={{ cursor: 'pointer' }}
        >
          <div className="st-stat-box__label">Current Tier</div>
          <div className="st-stat-box__value">{currentTier.tier + 1}/6</div>
        </div>
        <div
          className={`st-stat-box st-stat-box--tab ${activeStatsTab === 'achievements' ? 'st-stat-box--active' : ''}`}
          onClick={() => setActiveStatsTab(activeStatsTab === 'achievements' ? null : 'achievements')}
          role="tab"
          tabIndex={0}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setActiveStatsTab(activeStatsTab === 'achievements' ? null : 'achievements')}
          style={{ cursor: 'pointer' }}
        >
          <div className="st-stat-box__label">Achievements</div>
          <div className="st-stat-box__value">{achievements.length}</div>
        </div>
      </div>

      {/* Tab Content - Based on activeStatsTab */}

      {/* TAB 1: Total Points - Skill Tree Editor */}
      {activeStatsTab === 'points' && (
        <div className="st-tab-content st-tab-content--points">
          <SkillTreeVisualization ratings={ratings} isReadOnly={isReadOnly} />
        </div>
      )}

      {/* TAB 2: Skills Unlocked - Priority Skills Assignment */}
      {activeStatsTab === 'skills' && (
        <>
          <div className="st-tab-content st-tab-content--skills">
            {studentId ? (
              // Coach view: Show CoachPriorityAssignModal as section content
              <CoachPriorityAssignModal
                isOpen={true}
                onClose={() => setActiveStatsTab(null)}
                student={memoizedStudent}
                skillTree={null}
                onSave={() => setActiveStatsTab(null)}
                asSection={true}
              />
            ) : (
              // Player view: Show PrioritySkillsSection
              <PrioritySkillsSection playerId={playerId} user={user} />
            )}
          </div>
        </>
      )}

      {/* TAB 3: Current Tier - Path Walked and Path Ahead */}
      {activeStatsTab === 'tier' && TIER_STORIES[heroClass] && (
        <div className="st-tier-story" style={{ borderLeft: `6px solid ${classColor}` }}>
          <div className="st-tier-story__header" style={{ backgroundColor: `${classColor}10` }}>
            <div className="st-tier-story__header-content">
              <span className="st-tier-story__icon" style={{ fontSize: '36px', marginRight: '12px' }}>
                {currentTier.icon}
              </span>
              <h3 className="st-tier-story__title" style={{ color: classColor }}>
                {TIER_STORIES[heroClass][currentTier.tier]?.name || 'Your Journey'}
              </h3>
            </div>
            <button
              className="st-tier-story__close"
              onClick={() => setActiveStatsTab(null)}
              aria-label="Close story"
            >
              ✕
            </button>
          </div>

          {heroImage && (
            <div className="st-tier-story__hero-image">
              <img src={heroImage} alt={`${currentTier.name} - ${classInfo.label}`} />
            </div>
          )}

          <div className="st-tier-story__content">
            <div className="st-tier-story__section">
              <div className="st-tier-story__section-icon">📖</div>
              <h4 className="st-tier-story__subtitle" style={{ color: classColor }}>The Path You've Walked</h4>
              <p className="st-tier-story__text">
                {TIER_STORIES[heroClass][currentTier.tier]?.walked}
              </p>
            </div>

            <div className="st-tier-story__divider" style={{ borderColor: `${classColor}30` }} />

            <div className="st-tier-story__section">
              <div className="st-tier-story__section-icon">🗺️</div>
              <h4 className="st-tier-story__subtitle" style={{ color: classColor }}>The Path Ahead</h4>
              <p className="st-tier-story__text">
                {TIER_STORIES[heroClass][currentTier.tier]?.ahead}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* TAB 4: Achievements - Achievements List */}
      {activeStatsTab === 'achievements' && (
        <div className="st-tab-content st-tab-content--achievements">
          {achievements.length > 0 ? (
            <div className="st-achievements">
              <h3 className="st-achievements__title">🏅 Achievements Unlocked</h3>
              <div className="st-achievements__list">
                {achievements.map((achievement, idx) => (
                  <div
                    key={idx}
                    className={`st-achievement-badge ${isReadOnly ? 'st-achievement-badge--tappable' : ''}`}
                    onClick={() => isReadOnly && handleOpenAchievement(idx)}
                    role={isReadOnly ? 'button' : undefined}
                    tabIndex={isReadOnly ? 0 : undefined}
                    onKeyDown={isReadOnly ? (e => e.key === 'Enter' && handleOpenAchievement(idx)) : undefined}
                  >
                    <span className="st-achievement-badge__icon">{achievement.icon}</span>
                    <div className="st-achievement-badge__info">
                      <div className="st-achievement-badge__name">{achievement.name}</div>
                      <div className="st-achievement-badge__desc">{achievement.desc}</div>
                    </div>
                    {isReadOnly && <span className="st-achievement-badge__tap-hint">ℹ</span>}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="st-no-achievements">
              <p>No achievements unlocked yet. Keep training! 💪</p>
            </div>
          )}
        </div>
      )}

    </div>
  );
}
